/**
 * PrintFileReuseSection: the "Print file reuse" card (Phase 91, D-07).
 *
 * The product type codes that always get their own print file, even when the
 * design ID matches (the Upload Studio never shares a position for them).
 * Code-keyed settings rule: each code shows as `CODE · store names` from the
 * org's own code tables (`productSync.getCodeTablesForOrg`) and is added from
 * a pick-list of the org's codes not yet on the list; when the code tables
 * cannot load the card still works with manual entry and says so.
 *
 *  - Read: `productSync.getProductSyncSettings` (the list, [] when absent).
 *  - Write: `productSync.setPrintFileReuseExclusions` only, one Save for the
 *    whole list (never part of another settings save). The server normalizes
 *    and authorizes (Admin); this card only hides the controls for others.
 *  - Warehouse Coordinator: the list, read-only.
 *  - Phase 91 final fix D9: a failed read is a SectionError with Try again;
 *    Save and Discard show only once the list has changed.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Save, X } from 'lucide-react';
import { MAX_PRINT_FILE_REUSE_EXCLUDED_TYPES, PRODUCT_TYPE_CODE_RE } from '@threadheads/shared';
import { trpc } from '@/lib/trpc';
import { useRole } from '@/hooks/use-role';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SectionError } from '@/components/shared/SectionError';
import { PrintSizeCodeCombobox } from './PrintSizeCodeCombobox';
import { buildProductTypeCodeOptions, codeTablesStatus, joinStoreNames } from './org-code-labels';

const sameList = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((code, i) => code === b[i]);

export function PrintFileReuseSection(): JSX.Element {
  const { t } = useTranslation('settings');
  const { isAdmin } = useRole();
  const admin = isAdmin();
  const utils = trpc.useUtils();

  const settingsQuery = trpc.productSync.getProductSyncSettings.useQuery();
  const codeTablesQuery = trpc.productSync.getCodeTablesForOrg.useQuery({});

  const stored = useMemo(
    () => settingsQuery.data?.settings.printFileReuseExcludedTypes ?? [],
    [settingsQuery.data],
  );
  // null = untouched: the list is the stored one.
  const [draft, setDraft] = useState<string[] | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);
  const list = draft ?? stored;
  const changed = draft !== null && !sameList(draft, stored);

  const mutation = trpc.productSync.setPrintFileReuseExclusions.useMutation({
    onSuccess: (result) => {
      utils.productSync.getProductSyncSettings.setData(undefined, (old) =>
        old ? { ...old, settings: { ...old.settings, printFileReuseExcludedTypes: result.typeCodes } } : old,
      );
      void utils.productSync.getProductSyncSettings.invalidate();
      setDraft(null);
      setSaveFailed(false);
      toast.success(t('printFileReuse.saved'));
    },
    onError: () => setSaveFailed(true),
  });

  const { typeOptions, typeStoreNamesByCode } = useMemo(
    () =>
      buildProductTypeCodeOptions(codeTablesQuery.data?.productTypes, list, {
        custom: t('production.printSizeRules.badgeCustom'),
        singleSize: t('production.printSizeRules.badgeSingleSize'),
        alreadyAdded: t('printFileReuse.alreadyListed'),
        invalidCode: t('production.codeOptions.invalidCode'),
      }),
    [codeTablesQuery.data, list, t],
  );
  // The pick-list never offers a code already on the list.
  const pickOptions = useMemo(() => typeOptions.filter((o) => !list.includes(o.code)), [typeOptions, list]);
  const status = codeTablesStatus(codeTablesQuery, pickOptions.length);
  const tablesLoaded = !codeTablesQuery.isLoading && !codeTablesQuery.isError;
  const atLimit = list.length >= MAX_PRINT_FILE_REUSE_EXCLUDED_TYPES;

  const addCode = (raw: string): boolean => {
    const code = raw.trim().toUpperCase();
    if (!PRODUCT_TYPE_CODE_RE.test(code)) {
      toast.error(t('production.codeOptions.invalidCode'));
      return false;
    }
    if (list.includes(code)) return true;
    if (atLimit) return false;
    setDraft([...list, code]);
    setSaveFailed(false);
    return true;
  };

  const removeCode = (code: string): void => {
    setDraft(list.filter((c) => c !== code));
    setSaveFailed(false);
  };

  const rowLabel = (code: string): string | null => {
    const names = typeStoreNamesByCode.get(code);
    if (names && names.length > 0) return joinStoreNames(names);
    // Code tables down or still loading: the bare code.
    return tablesLoaded ? t('printFileReuse.notInTables') : null;
  };

  if (settingsQuery.isLoading) {
    return (
      <div className="space-y-2" data-testid="print-file-reuse-loading">
        <Skeleton className="h-10 w-full rounded-md" />
        <Skeleton className="h-10 w-full rounded-md" />
      </div>
    );
  }

  if (settingsQuery.isError) {
    return (
      <SectionError
        variant="inline"
        title={t('printFileReuse.loadError')}
        onRetry={() => void settingsQuery.refetch()}
        retrying={settingsQuery.isFetching}
        retryLabel={t('printFileReuse.retry')}
      />
    );
  }

  return (
    <div className="space-y-4">
      {list.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('printFileReuse.empty')}</p>
      ) : (
        <ul className="divide-y rounded-md border" aria-label={t('printFileReuse.listLabel')}>
          {list.map((code) => {
            const label = rowLabel(code);
            return (
              <li key={code} className="flex min-h-10 items-center gap-2 px-3 py-1.5 text-sm">
                <span className="min-w-0 flex-1 whitespace-normal break-words">
                  <span className="font-mono">{code}</span>
                  {label && (
                    <>
                      <span aria-hidden="true" className="px-1.5 text-muted-foreground">
                        ·
                      </span>
                      <span className="text-muted-foreground">{label}</span>
                    </>
                  )}
                </span>
                {admin && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 shrink-0"
                    aria-label={t('printFileReuse.remove', { code })}
                    onClick={() => removeCode(code)}
                    disabled={mutation.isPending}
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!admin && <p className="text-sm text-muted-foreground">{t('printFileReuse.readOnly')}</p>}

      {admin && (
        <>
          {atLimit ? (
            <p className="text-sm text-muted-foreground">
              {t('printFileReuse.limit', { count: MAX_PRINT_FILE_REUSE_EXCLUDED_TYPES })}
            </p>
          ) : (
            <div className="space-y-1">
              <PrintSizeCodeCombobox
                options={pickOptions}
                status={status === 'loading' ? 'error' : status}
                onSelect={addCode}
                placeholder={t('printFileReuse.pickerPlaceholder')}
                ariaLabel={t('printFileReuse.pickerAria')}
                addButtonLabel={t('printFileReuse.add')}
                createLabel={(code) => t('printFileReuse.create', { code })}
                emptyLabel={t('printFileReuse.pickerEmpty')}
                isValidCode={(code) => PRODUCT_TYPE_CODE_RE.test(code)}
              />
              {codeTablesQuery.isError && (
                <p className="text-xs text-muted-foreground">{t('printFileReuse.codeTablesDown')}</p>
              )}
            </div>
          )}

          {saveFailed && (
            <p role="alert" className="text-sm text-destructive">
              {t('printFileReuse.saveError')}
            </p>
          )}

          {changed && (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setDraft(null);
                  setSaveFailed(false);
                }}
                disabled={mutation.isPending}
                className="w-full sm:w-auto"
              >
                {t('printFileReuse.discard')}
              </Button>
              <Button
                type="button"
                onClick={() => mutation.mutate({ typeCodes: list })}
                disabled={mutation.isPending}
                className="w-full gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90 motion-reduce:transition-none sm:w-auto"
              >
                {mutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                ) : (
                  <Save className="h-4 w-4" aria-hidden="true" />
                )}
                {mutation.isPending ? t('printFileReuse.saving') : t('printFileReuse.save')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
